<template>
  <div class="language-picker" v-click-outside="onClickOutside">
    <button
      type="button"
      class="language-picker__trigger"
      :class="{ 'language-picker__trigger--open': isOpen }"
      :aria-expanded="isOpen ? 'true' : 'false'"
      aria-haspopup="listbox"
      @click="toggleOpen"
    >
      <span class="language-picker__trigger-label">{{ displayLabel }}</span>
      <v-icon class="language-picker__chevron" aria-hidden="true">
        {{ isOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}
      </v-icon>
    </button>

    <div v-show="isOpen" class="language-picker__menu">
      <template v-if="can_translate">
        <button
          type="button"
          class="language-picker__row language-picker__row--translate"
          @click.stop="onTranslateClick"
        >
          <span class="language-picker__translate-icon" aria-hidden="true">
            <Icon name="language_translate" size="extra-small" />
          </span>
          <span class="language-picker__row-text">Translate</span>
        </button>
      </template>

      <ul class="language-picker__list" role="listbox" aria-label="Languages">
        <li
          v-for="lang in mergedLanguages"
          :key="languageKey(lang)"
          role="option"
          :aria-selected="isSelected(lang) ? 'true' : 'false'"
          class="language-picker__row language-picker__row--lang"
          :class="{ 'language-picker__row--selected': isSelected(lang) }"
          @click.stop="selectLanguage(lang)"
        >
          <span class="language-picker__row-text">{{ lang.name }}</span>
          <v-icon v-if="isSelected(lang)" class="language-picker__check" aria-hidden="true">
            mdi-check
          </v-icon>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: "LanguagePicker",
  model: {
    prop: "modelValue",
    event: "update:modelValue",
  },
};
</script>

<script setup>
import { computed, ref } from "vue";
import Icon from "@components/icons/Icon.vue";

const ENGLISH_DEFAULT = Object.freeze({
  id: 1,
  name: "English",
  code: "en",
});

const props = defineProps({
  modelValue: {
    type: Object,
    default: null,
  },
  can_translate: {
    type: Boolean,
    default: false,
  },
  available_languages: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["update:modelValue", "input", "translate"]);

const isOpen = ref(false);

function ensureEnglishInList(list) {
  const hasEn = list.some((l) => l && String(l.code).toLowerCase() === "en");
  if (hasEn) {
    return list;
  }
  return [ENGLISH_DEFAULT, ...list];
}

const mergedLanguages = computed(() => {
  const source =
    props.available_languages && props.available_languages.length > 0
      ? props.available_languages
      : [ENGLISH_DEFAULT];
  return ensureEnglishInList(source);
});

const effectiveSelection = computed(() => {
  if (props.modelValue && typeof props.modelValue === "object" && props.modelValue.code) {
    return props.modelValue;
  }
  return (
    mergedLanguages.value.find((l) => String(l.code).toLowerCase() === "en") || ENGLISH_DEFAULT
  );
});

const displayLabel = computed(() => effectiveSelection.value.name || ENGLISH_DEFAULT.name);

function languageKey(lang) {
  return lang.id != null ? lang.id : lang.code;
}

function isSelected(lang) {
  return String(effectiveSelection.value.code).toLowerCase() === String(lang.code).toLowerCase();
}

function toggleOpen() {
  isOpen.value = !isOpen.value;
}

function onClickOutside() {
  isOpen.value = false;
}

function selectLanguage(lang) {
  if (lang.disabled) return;
  emit("update:modelValue", lang);
  /** Vue 2 default v-model listens to `input`; keeps parents working without `model` option. */
  emit("input", lang);
  isOpen.value = false;
}

function onTranslateClick() {
  emit("translate");
  isOpen.value = false;
}
</script>

<style scoped>
.language-picker {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: stretch;
  align-self: stretch;
  min-width: 200px;
  font-family: Inter, sans-serif;
}

.language-picker__trigger {
  display: flex;
  box-sizing: border-box;
  height: 28px;
  padding: 17px 6px 17px 8px;
  align-items: center;
  gap: var(--spacing-md, 8px);
  align-self: stretch;
  margin: 0;
  width: 100%;
  border-radius: var(--radius-md, 8px);
  border: 1px solid var(--Stroke, #d0d5dd);
  background: var(--White, #fff);
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
  cursor: pointer;
  text-align: left;
  color: #101828;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
}

.language-picker__trigger:hover,
.language-picker__trigger:focus-visible {
  outline: none;
  border-color: var(--Stroke, #d0d5dd);
}

.language-picker__trigger-label {
  flex: 1 0 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.language-picker__chevron {
  flex-shrink: 0;
  font-size: 16px !important;
  width: 16px !important;
  height: 16px !important;
  color: #667085;
}

/* Menu / dropdown panel */
.language-picker__menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
  width: 100%;
  min-width: 100%;
  max-height: 280px;
  overflow-x: hidden;
  overflow-y: auto;
  padding: var(--spacing-md, 8px);
  margin: 0;
  box-sizing: border-box;
  list-style: none;
  border-radius: var(--radius-md, 8px);
  border: 1px solid #eaecf0;
  background: var(--White, #fff);
  box-shadow:
    0 12px 16px -4px rgba(16, 24, 40, 0.08),
    0 4px 6px -2px rgba(16, 24, 40, 0.03);
  gap: var(--spacing-sm, 6px);
}

.language-picker__menu::-webkit-scrollbar {
  width: 6px;
}

.language-picker__menu::-webkit-scrollbar-thumb {
  background: #eaecf0;
  border-radius: 3px;
}

.language-picker__list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
  gap: var(--spacing-sm, 6px);
}

/* Shared row reset */
.language-picker__row {
  box-sizing: border-box;
  margin: 0;
  border: none;
  cursor: pointer;
  text-align: left;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
  color: #101828;
}

/* Rows: no default fill — only the menu panel is white; items sit on transparent until hover/selected */
.language-picker__row--translate,
.language-picker__row--lang {
  display: flex;
  align-items: center;
  gap: var(--spacing-md, 8px);
  flex: 1 0 0;
  align-self: stretch;
  width: 100%;
  padding: 10px 10px 10px var(--spacing-md, 8px);
  border-radius: var(--spacing-sm, 6px);
  background: transparent;
}

.language-picker__row--translate {
  justify-content: flex-start;
}

.language-picker__row--lang {
  justify-content: space-between;
}

.language-picker__row--translate:hover,
.language-picker__row--translate:focus-visible,
.language-picker__row--lang:hover:not(.language-picker__row--selected),
.language-picker__row--lang:focus-visible:not(.language-picker__row--selected) {
  outline: none;
  background: #f4ebff;
}

/* Selected language row */
.language-picker__row--lang.language-picker__row--selected {
  border-radius: var(--spacing-sm, 6px);
  background: #f9fafb;
}

.language-picker__row--lang.language-picker__row--selected:hover,
.language-picker__row--lang.language-picker__row--selected:focus-visible {
  outline: none;
  background: #f4ebff;
}

.language-picker__row-text {
  flex: 1 0 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.language-picker__translate-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
}

.language-picker__check {
  flex-shrink: 0;
  font-size: 16px !important;
  width: 16px !important;
  height: 16px !important;
  color: #7f56d9 !important;
}
</style>
